/**
 * Le module **Produits communautaires** — la supervision des tontines, et leur
 * dissolution à deux signatures (lot 5 de `docs/PLAN_TONTINE.md`, `EX-TON-014`).
 *
 * Maquette : artboard `b_produits`. Le dessin porte cinq produits ; un seul est
 * livré, la tontine, et l'écran ne montre que lui plutôt que d'inventer des
 * comptes joints et des quêtes qu'aucune route ne sert.
 *
 * ## CE QUI DOIT SAUTER AUX YEUX
 *
 * Trois choses, et le serveur les range déjà en tête de la liste :
 *
 * 1. **un compte qui ne s'explique pas** — le solde du sous-compte `TON` au
 *    grand livre diffère des cautions détenues plus les cotisations encore sur
 *    le compte. C'est l'invariant d'AD-30. Un écart est une ALERTE, dite par un
 *    mot et non par une seule couleur ;
 * 2. **une tontine SUSPENDUE** — un tour reporté au-delà du délai, et c'est le
 *    back-office qui décide de la suite ;
 * 3. **un tour REPORTÉ** — un bénéficiaire attend un pot qu'une cotisation
 *    manquante bloque.
 *
 * ## LA DISSOLUTION N'EST PAS EXÉCUTÉE PAR CE BOUTON
 *
 * `TONTINE_DISSOLUTION` est en double validation. Le socle rend `202` avec
 * l'identifiant d'une demande, et **aucun membre n'a encore rien récupéré**.
 * Annoncer « tontine dissoute » ici serait annoncer des restitutions qui n'ont
 * pas eu lieu — le défaut même de l'exécuteur-qui-n'exécute-rien, que ce dépôt a
 * payé deux fois. L'écran dit donc « demande en attente d'une seconde
 * signature ».
 *
 * Le bouton n'est offert que sur une tontine démarrée et pas encore close
 * (`dissolvable`) : une tontine ouverte se dissout par son organisateur, depuis
 * l'application, et une tontine terminée n'a plus rien à rendre.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import {
  fetchTontine,
  fetchTontines,
  postponedFilter,
  requestTontineDissolution,
} from '../http/tontines.js';
import type { TontineDetail, TontinePage, TontineRow } from '../http/tontines.js';

/** Les filtres de la liste, dans l'ordre de lecture : les alertes d'abord. */
const FILTRES: readonly (string | null)[] = [
  null,
  'SUSPENDUE',
  postponedFilter,
  'ACTIVE',
  'OUVERTE',
  'TERMINEE',
  'DISSOUTE',
];

/** La clé d'un message d'erreur, quelle que soit la forme de l'échec. */
function cleDErreur(echec: unknown): string {
  return echec instanceof ApiError ? echec.messageKey : 'errors.server';
}

/** Ce qui met une ligne en avant — l'écart d'abord, comme le serveur la range. */
function alerteDe(ligne: TontineRow): string | undefined {
  if (ligne.balanceMismatch) return 'mismatch';
  if (ligne.status === 'SUSPENDUE') return 'suspended';
  if (ligne.hasPostponedRound) return 'postponed';
  return undefined;
}

/** Le module de supervision des tontines. */
export function TontinesScreen(): ReactNode {
  const { t, i18n } = useTranslation();
  const client = useApiClient();

  const [filtre, setFiltre] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [liste, setListe] = useState<TontinePage | null>(null);
  const [erreurListe, setErreurListe] = useState<string | null>(null);
  const [choisie, setChoisie] = useState<string | null>(null);
  const [fiche, setFiche] = useState<TontineDetail | null>(null);
  const [erreurFiche, setErreurFiche] = useState<string | null>(null);
  const [dissolution, setDissolution] = useState(false);
  const [erreurGeste, setErreurGeste] = useState<string | null>(null);
  const [avis, setAvis] = useState<string | null>(null);

  const montant = useCallback(
    (mineures: number | null): string =>
      mineures === null
        ? t('tontines.none')
        : t('tontines.amount', { value: new Intl.NumberFormat(i18n.language).format(mineures) }),
    [i18n.language, t],
  );

  useEffect(() => {
    let vivant = true;
    fetchTontines(client, filtre, page).then(
      (lue) => {
        if (!vivant) return;
        setListe(lue);
        setErreurListe(null);
      },
      (echec: unknown) => {
        if (!vivant) return;
        setListe(null);
        setErreurListe(cleDErreur(echec));
      },
    );
    return () => {
      vivant = false;
    };
  }, [client, filtre, page]);

  const relireLaFiche = useCallback(
    async (id: string): Promise<void> => {
      try {
        setFiche(await fetchTontine(client, id));
        setErreurFiche(null);
      } catch (echec) {
        setFiche(null);
        setErreurFiche(cleDErreur(echec));
      }
    },
    [client],
  );

  useEffect(() => {
    if (choisie === null) return;
    void relireLaFiche(choisie);
  }, [choisie, relireLaFiche]);

  const champsDeDissolution: readonly FormFieldDescriptor[] = useMemo(
    () => [
      {
        name: 'reason',
        labelKey: 'tontines.dissolve.reason',
        control: 'text',
        placeholderKey: 'tontines.dissolve.reasonPlaceholder',
        required: true,
      },
    ],
    [],
  );

  const demanderLaDissolution = useCallback(
    async (valeurs: Readonly<Record<string, string>>): Promise<void> => {
      if (fiche === null) return;
      try {
        const issue = await requestTontineDissolution(client, {
          tontineId: fiche.id,
          reason: valeurs.reason ?? '',
        });
        setErreurGeste(null);
        setDissolution(false);
        // **`outcome` est lu, pas supposé.** En double validation, le socle rend
        // 202 et l'identifiant d'une demande : rien n'a été rendu à personne.
        setAvis(
          issue.outcome === 'EXECUTED'
            ? (issue.detail ?? t('tontines.dissolve.executed'))
            : t('tontines.dissolve.pending', { id: issue.approvalRequestId ?? '' }),
        );
      } catch (echec) {
        setErreurGeste(cleDErreur(echec));
      }
    },
    [client, fiche, t],
  );

  const pages = liste === null ? 1 : Math.max(1, Math.ceil(liste.total / liste.pageSize));

  return (
    <section aria-label={t('tontines.landmark')} className="tontines">
      <p className="tontines__journal-notice">{t('tontines.journalNotice')}</p>

      {avis === null ? null : (
        <p className="tontines__avis" role="status">
          {avis}
        </p>
      )}

      {/* --- Le bandeau des trois alertes, sur TOUTES les tontines --- */}
      {liste === null ? null : (
        <ul className="tontines__alerts" data-testid="tontines-alerts">
          <li className="tontines__alert" data-tone={liste.mismatchCount > 0 ? 'danger' : undefined}
              data-testid="tontines-alert-mismatch">
            <span className="tontines__alert-value">{liste.mismatchCount}</span>
            <span className="tontines__alert-label">{t('tontines.alerts.mismatch')}</span>
          </li>
          <li className="tontines__alert" data-tone={liste.suspendedCount > 0 ? 'warning' : undefined}
              data-testid="tontines-alert-suspended">
            <span className="tontines__alert-value">{liste.suspendedCount}</span>
            <span className="tontines__alert-label">{t('tontines.alerts.suspended')}</span>
          </li>
          <li className="tontines__alert" data-tone={liste.postponedCount > 0 ? 'warning' : undefined}
              data-testid="tontines-alert-postponed">
            <span className="tontines__alert-value">{liste.postponedCount}</span>
            <span className="tontines__alert-label">{t('tontines.alerts.postponed')}</span>
          </li>
        </ul>
      )}

      <section aria-label={t('tontines.listTitle')} className="tontines__panel">
        <h2 className="tontines__section-title">{t('tontines.listTitle')}</h2>
        <div aria-label={t('tontines.filtersLabel')} className="tontines__filters" role="group">
          {FILTRES.map((valeur) => (
            <button
              className="tontines__filter"
              data-chosen={filtre === valeur}
              data-testid={`tontines-filter-${valeur ?? 'ALL'}`}
              key={valeur ?? 'ALL'}
              onClick={() => {
                setFiltre(valeur);
                setPage(1);
              }}
              type="button"
            >
              {t(`tontines.filters.${valeur ?? 'ALL'}`)}
            </button>
          ))}
        </div>

        {erreurListe !== null ? (
          <p className="tontines__notice" role="alert">
            {t(erreurListe)}
          </p>
        ) : liste === null ? (
          <p className="tontines__meta">{t('tontines.loading')}</p>
        ) : liste.tontines.length === 0 ? (
          <p className="tontines__meta" data-testid="tontines-empty">
            {t('tontines.empty')}
          </p>
        ) : (
          <>
            <div className="tontines__table-frame">
              <table className="tontines__table" data-testid="tontines-table">
                <thead>
                  <tr>
                    <th scope="col">{t('tontines.columns.name')}</th>
                    <th scope="col">{t('tontines.columns.organizer')}</th>
                    <th scope="col">{t('tontines.columns.members')}</th>
                    <th scope="col">{t('tontines.columns.contribution')}</th>
                    <th scope="col">{t('tontines.columns.round')}</th>
                    <th scope="col">{t('tontines.columns.balance')}</th>
                    <th scope="col">{t('tontines.columns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {liste.tontines.map((ligne) => (
                    <tr data-alert={alerteDe(ligne)} data-testid={`tontine-${ligne.id}`} key={ligne.id}>
                      <th className="tontines__name-cell" scope="row">
                        <button
                          className="tontines__open"
                          onClick={() => {
                            setChoisie(ligne.id);
                            setDissolution(false);
                            setAvis(null);
                          }}
                          type="button"
                        >
                          {ligne.name}
                        </button>
                      </th>
                      <td>
                        <span className="tontines__strong">
                          {ligne.organizerName ?? t('tontines.unnamed')}
                        </span>
                        <span className="tontines__minor">{ligne.organizerMsisdn ?? t('tontines.none')}</span>
                      </td>
                      <td data-emphasis="numeric">
                        {t('tontines.memberCount', { count: ligne.memberCount, max: ligne.maxMembers })}
                      </td>
                      <td data-emphasis="numeric">
                        {montant(ligne.contributionAmount)}
                        <span className="tontines__minor">{t(`tontines.period.${ligne.period}`)}</span>
                      </td>
                      <td>
                        {ligne.currentRoundNumber === null ? (
                          t('tontines.noRound')
                        ) : (
                          <>
                            {t('tontines.roundLine', {
                              number: ligne.currentRoundNumber,
                              due: ligne.currentRoundDueOn ?? '',
                            })}
                            <span
                              className="tontines__minor"
                              data-tone={ligne.currentRoundStatus === 'REPORTE' ? 'warning' : undefined}
                            >
                              {t(`tontines.roundStatus.${ligne.currentRoundStatus ?? 'A_VENIR'}`)}
                            </span>
                          </>
                        )}
                      </td>
                      <td data-emphasis="numeric">
                        {montant(ligne.ledgerBalance)}
                        {/* **L'écart se dit par un MOT.** La teinte ne fait que doubler. */}
                        <span className="tontines__minor" data-tone={ligne.balanceMismatch ? 'danger' : undefined}>
                          {ligne.balanceMismatch
                            ? t('tontines.mismatchShort', { expected: montant(ligne.expectedBalance) })
                            : t('tontines.consistentShort')}
                        </span>
                      </td>
                      <td>
                        <span
                          className="tontines__status"
                          data-tone={ligne.status === 'SUSPENDUE' ? 'warning' : undefined}
                        >
                          {t(`tontines.status.${ligne.status}`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tontines__pages">
              <button
                className="tontines__filter"
                disabled={page <= 1}
                onClick={() => {
                  setPage(page - 1);
                }}
                type="button"
              >
                {t('tontines.previous')}
              </button>
              <span className="tontines__meta" data-testid="tontines-page">
                {t('tontines.pageOf', { page, pages, total: liste.total })}
              </span>
              <button
                className="tontines__filter"
                disabled={page >= pages}
                onClick={() => {
                  setPage(page + 1);
                }}
                type="button"
              >
                {t('tontines.next')}
              </button>
            </div>
          </>
        )}
      </section>

      {/* --- La fiche --- */}
      {choisie === null ? null : erreurFiche !== null ? (
        <p className="tontines__notice" role="alert">
          {t(erreurFiche)}
        </p>
      ) : fiche === null ? (
        <p className="tontines__meta">{t('tontines.loading')}</p>
      ) : (
        <TontineSheet
          dissolution={dissolution}
          erreurGeste={erreurGeste}
          fiche={fiche}
          montant={montant}
          onCancel={() => {
            setDissolution(false);
            setErreurGeste(null);
          }}
          onDissolve={() => {
            setDissolution(true);
            setErreurGeste(null);
            setAvis(null);
          }}
          onSubmit={demanderLaDissolution}
          champs={champsDeDissolution}
        />
      )}
    </section>
  );
}

/** La fiche d'une tontine. */
function TontineSheet({
  champs,
  dissolution,
  erreurGeste,
  fiche,
  montant,
  onCancel,
  onDissolve,
  onSubmit,
}: {
  readonly champs: readonly FormFieldDescriptor[];
  readonly dissolution: boolean;
  readonly erreurGeste: string | null;
  readonly fiche: TontineDetail;
  readonly montant: (mineures: number | null) => string;
  readonly onCancel: () => void;
  readonly onDissolve: () => void;
  readonly onSubmit: (valeurs: Readonly<Record<string, string>>) => Promise<void>;
}): ReactNode {
  const { t } = useTranslation();
  const solde = fiche.balance;
  const courant = fiche.currentRound;

  return (
    <section aria-label={t('tontines.sheet.landmark', { name: fiche.name })}
             className="tontines__panel" data-testid="tontine-sheet">
      <div className="tontines__sheet-head">
        <h2 className="tontines__section-title">{fiche.name}</h2>
        <span className="tontines__status" data-tone={fiche.status === 'SUSPENDUE' ? 'warning' : undefined}>
          {t(`tontines.status.${fiche.status}`)}
        </span>
        {fiche.dissolvable ? (
          <button className="tontines__danger" data-testid="tontine-dissolve" onClick={onDissolve} type="button">
            {t('tontines.dissolve.open')}
          </button>
        ) : null}
      </div>
      <p className="tontines__meta">
        {t('tontines.sheet.rules', {
          amount: montant(fiche.contributionAmount),
          period: t(`tontines.period.${fiche.period}`),
          draw: t(`tontines.drawMode.${fiche.drawMode}`),
          max: fiche.maxMembers,
        })}
      </p>
      {fiche.dissolutionRef === null ? null : (
        <p className="tontines__meta" data-testid="tontine-dissolution-ref">
          {t('tontines.sheet.dissolutionRef', { ref: fiche.dissolutionRef })}
        </p>
      )}

      {dissolution ? (
        <div className="tontines__dissolve-form" data-testid="tontine-dissolve-form">
          <h3 className="tontines__subtitle">{t('tontines.dissolve.title', { name: fiche.name })}</h3>
          <p className="tontines__meta">{t('tontines.dissolve.rule')}</p>
          {erreurGeste === null ? null : (
            <p className="tontines__notice" role="alert">
              {t(erreurGeste)}
            </p>
          )}
          <FormShell fields={champs} layout="stacked" onSubmit={onSubmit}
                     submitLabelKey="tontines.dissolve.submit" />
          <button className="tontines__filter" onClick={onCancel} type="button">
            {t('tontines.dissolve.cancel')}
          </button>
        </div>
      ) : null}

      {/* --- Le compte, et l'invariant d'AD-30 --- */}
      <div className="tontines__balance" data-tone={solde.consistent ? undefined : 'danger'}
           data-testid="tontine-balance">
        <h3 className="tontines__subtitle">{t('tontines.balance.title')}</h3>
        <dl className="tontines__facts">
          <dt>{t('tontines.balance.account')}</dt>
          <dd>{solde.accountNumber ?? t('tontines.none')}</dd>
          <dt>{t('tontines.balance.ledger')}</dt>
          <dd data-emphasis="numeric">{montant(solde.ledgerBalance)}</dd>
          <dt>{t('tontines.balance.deposits', { count: solde.heldDepositCount })}</dt>
          <dd data-emphasis="numeric">{montant(solde.heldDeposits)}</dd>
          <dt>{t('tontines.balance.contributions', { count: solde.pendingContributionCount })}</dt>
          <dd data-emphasis="numeric">{montant(solde.pendingContributions)}</dd>
          <dt>{t('tontines.balance.expected')}</dt>
          <dd data-emphasis="numeric">{montant(solde.expected)}</dd>
        </dl>
        {solde.consistent ? (
          <p className="tontines__ok" data-testid="tontine-balance-ok">{t('tontines.balance.consistent')}</p>
        ) : (
          <p className="tontines__notice" data-testid="tontine-balance-gap" role="alert">
            {t('tontines.balance.gap', { gap: montant(solde.gap) })}
          </p>
        )}
      </div>

      {/* --- Les membres --- */}
      <h3 className="tontines__subtitle">{t('tontines.members.title', { count: fiche.members.length })}</h3>
      <div className="tontines__table-frame">
        <table className="tontines__table" data-testid="tontine-members">
          <thead>
            <tr>
              <th scope="col">{t('tontines.members.position')}</th>
              <th scope="col">{t('tontines.members.identity')}</th>
              <th scope="col">{t('tontines.members.msisdn')}</th>
              <th scope="col">{t('tontines.members.status')}</th>
              <th scope="col">{t('tontines.members.deposit')}</th>
            </tr>
          </thead>
          <tbody>
            {fiche.members.map((membre) => (
              <tr data-testid={`member-${membre.memberId}`} key={membre.memberId}>
                <td data-emphasis="numeric">{membre.position ?? t('tontines.none')}</td>
                <th className="tontines__name-cell" scope="row">
                  <span className="tontines__strong">
                    {membre.firstName === null && membre.lastName === null
                      ? membre.displayName
                      : `${membre.firstName ?? ''} ${membre.lastName ?? ''}`.trim()}
                  </span>
                  <span className="tontines__minor">
                    {membre.organizer
                      ? t('tontines.members.organizer', { ref: membre.publicRef ?? '' })
                      : (membre.publicRef ?? '')}
                  </span>
                </th>
                <td>{membre.msisdn ?? t('tontines.none')}</td>
                <td>{t(`tontines.memberStatus.${membre.status}`)}</td>
                <td>
                  <span className="tontines__minor" data-tone={membre.depositStatus === 'CONSOMMEE' ? 'warning' : undefined}>
                    {t(`tontines.depositStatus.${membre.depositStatus}`)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- Les tours --- */}
      <h3 className="tontines__subtitle">{t('tontines.rounds.title')}</h3>
      <div className="tontines__table-frame">
        <table className="tontines__table" data-testid="tontine-rounds">
          <thead>
            <tr>
              <th scope="col">{t('tontines.rounds.number')}</th>
              <th scope="col">{t('tontines.rounds.dueOn')}</th>
              <th scope="col">{t('tontines.rounds.status')}</th>
              <th scope="col">{t('tontines.rounds.beneficiary')}</th>
              <th scope="col">{t('tontines.rounds.pot')}</th>
              <th scope="col">{t('tontines.rounds.fee')}</th>
              <th scope="col">{t('tontines.rounds.net')}</th>
            </tr>
          </thead>
          <tbody>
            {fiche.rounds.map((tour) => (
              <tr data-alert={tour.status === 'REPORTE' ? 'postponed' : undefined}
                  data-testid={`round-${tour.number}`} key={tour.id}>
                <td data-emphasis="numeric">{tour.number}</td>
                <td>{tour.dueOn}</td>
                <td>
                  <span className="tontines__minor" data-tone={tour.status === 'REPORTE' ? 'warning' : undefined}>
                    {t(`tontines.roundStatus.${tour.status}`)}
                  </span>
                </td>
                <td>{tour.beneficiaryName ?? t('tontines.none')}</td>
                <td data-emphasis="numeric">{montant(tour.payoutAmount)}</td>
                <td data-emphasis="numeric">{montant(tour.payoutFee)}</td>
                <td data-emphasis="numeric">{montant(tour.netAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- Les cotisations du tour en cours --- */}
      <h3 className="tontines__subtitle">
        {courant === null
          ? t('tontines.current.none')
          : t('tontines.current.title', {
              number: courant.number,
              count: courant.contributions.length,
              expected: courant.expectedCount,
            })}
      </h3>
      {courant === null ? null : (
        <div className="tontines__table-frame">
          <table className="tontines__table" data-testid="tontine-contributions">
            <thead>
              <tr>
                <th scope="col">{t('tontines.current.member')}</th>
                <th scope="col">{t('tontines.current.amount')}</th>
                <th scope="col">{t('tontines.current.source')}</th>
                <th scope="col">{t('tontines.current.paidAt')}</th>
                <th scope="col">{t('tontines.current.refund')}</th>
              </tr>
            </thead>
            <tbody>
              {courant.contributions.map((cotisation) => (
                <tr data-testid={`contribution-${cotisation.memberId}`} key={cotisation.memberId}>
                  <td>{cotisation.memberName ?? t('tontines.none')}</td>
                  <td data-emphasis="numeric">{montant(cotisation.amount)}</td>
                  <td>{t(`tontines.source.${cotisation.source}`)}</td>
                  <td>{cotisation.paidAt ?? t('tontines.none')}</td>
                  <td>
                    {cotisation.refundedAt === null
                      ? t('tontines.current.notRefunded')
                      : t('tontines.current.refunded', { at: cotisation.refundedAt })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Les mouvements de caution --- */}
      <h3 className="tontines__subtitle">{t('tontines.movements.title')}</h3>
      {fiche.depositMovements.length === 0 ? (
        <p className="tontines__meta">{t('tontines.movements.empty')}</p>
      ) : (
        <div className="tontines__table-frame">
          <table className="tontines__table" data-testid="tontine-movements">
            <thead>
              <tr>
                <th scope="col">{t('tontines.movements.at')}</th>
                <th scope="col">{t('tontines.movements.member')}</th>
                <th scope="col">{t('tontines.movements.kind')}</th>
                <th scope="col">{t('tontines.movements.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {fiche.depositMovements.map((mouvement, rang) => (
                <tr key={`${mouvement.memberId}-${String(rang)}`}>
                  <td>{mouvement.occurredAt ?? t('tontines.none')}</td>
                  <td>{mouvement.memberName ?? t('tontines.none')}</td>
                  <td>
                    {mouvement.roundNumber === null
                      ? t(`tontines.movementKind.${mouvement.kind}`)
                      : t('tontines.movements.kindForRound', {
                          kind: t(`tontines.movementKind.${mouvement.kind}`),
                          number: mouvement.roundNumber,
                        })}
                  </td>
                  <td data-emphasis="numeric">{montant(mouvement.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
